// composables
import { computed, type Ref } from 'vue'
// utilities
import { alertToast } from '@/utilities/notification'
// composables
import useApiRequest from '@/composables/useApiRequest'
// types
import type { Agreement } from '@/types/domain/agreement'

/**
 * Composable for managing agreement tags
 * Handles fetching tags, adding/removing tags from agreements
 */
export default function useAgreementTags(agreementID: Ref<string | number>) {
  // state
  const allTags = useApiRequest()
  const tagApiHandler = useApiRequest()

  /**
   * Fetches all available tags
   */
  function fetchAllTags(): void {
    allTags.send({ endpoint: '/v1/tags' })
  }

  /**
   * Computed list of active tags
   */
  const allTagsList = computed(() => {
    if (!allTags.data.value || !Array.isArray(allTags.data.value)) {
      return []
    }
    return allTags.data.value.filter((tag: { isActive: number }) => tag.isActive === 1)
  })

  /**
   * Computes list of tags to display for the agreement
   * @param agreementData - The agreement data with tagIDs
   * @returns Array of tag objects to display
   */
  function getTagsToDisplay(
    agreementData: Agreement | null
  ): Array<{ id: number; title: string; displayStyle: string }> {
    if (!agreementData?.tagIDs) {
      return []
    }

    const matchedTags: Array<{ id: number; title: string; displayStyle: string }> = []

    agreementData.tagIDs.forEach(tagID => {
      if (allTags.data.value && Array.isArray(allTags.data.value)) {
        const tagData = allTags.data.value.find((tag: { id: number }) => tag.id === tagID)

        if (tagData) {
          matchedTags.push(tagData)
        }
      }
    })

    return matchedTags
  }

  /**
   * Removes a tag from the agreement
   * @param tagID - The ID of the tag to remove
   * @param agreementData - The agreement data ref to update
   */
  function removeTag(tagID: number, agreementData: Ref<Agreement>): void {
    tagApiHandler
      .send({
        endpoint: '/v1/agreements/' + agreementID.value + '/tags' + '/' + tagID,
        method: 'DELETE'
      })
      .then(response => {
        if (response.success) {
          // Remove from tags array
          const tagIndex = agreementData.value.tagIDs.findIndex(item => item === tagID)
          if (tagIndex > -1) {
            agreementData.value.tagIDs.splice(tagIndex, 1)
          }
        } else {
          alertToast('Failed to remove tag', response.message, 'error')
        }
      })
  }

  /**
   * Adds a tag to the agreement
   * @param tagID - The ID of the tag to add
   */
  function addTag(tagID: number): void {
    tagApiHandler
      .send({
        endpoint: '/v1/agreements/' + agreementID.value + '/tags',
        method: 'POST',
        data: JSON.stringify({ tagId: tagID })
      })
      .then(response => {
        if (!response.success) {
          alertToast('Failed to add tag', response.message, 'error')
        }
      })
  }

  return {
    // state
    allTags,
    // computed
    allTagsList,
    // methods
    fetchAllTags,
    getTagsToDisplay,
    removeTag,
    addTag
  }
}
